import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, Users, ClipboardList, FileText, Plus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function StaffDashboard() {
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    (async () => {
      const s = await api.get("/analytics/staff");
      const c = await api.get("/courses");
      setStats(s.data); setCourses(c.data);
    })();
  }, []);

  return (
    <Layout title="Staff Studio" subtitle="Staff · Overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Courses" value={stats?.total_courses ?? "—"} icon={BookOpen} color="blue" />
        <Kpi label="Students" value={stats?.total_students ?? "—"} icon={Users} color="emerald" />
        <Kpi label="Assignments" value={stats?.total_assignments ?? "—"} icon={ClipboardList} color="violet" />
        <Kpi label="Quizzes" value={stats?.total_quizzes ?? "—"} icon={FileText} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Course performance</div>
              <h2 className="font-display font-bold text-xl text-slate-900">Students per course</h2>
            </div>
            <Link to="/staff/create" data-testid="quick-create-course" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700">
              <Plus className="w-4 h-4" /> New course
            </Link>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={stats?.per_course || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="course" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip />
                <Bar dataKey="students" fill="#2563EB" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Your courses</div>
          <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Recent</h3>
          <div className="space-y-2">
            {courses.slice(0, 6).map((c) => (
              <Link key={c.id} to={`/courses/${c.id}`} data-testid={`staff-course-${c.id}`} className="block border border-slate-100 rounded-xl p-3 hover:border-blue-300">
                <div className="text-sm font-semibold text-slate-900 line-clamp-1">{c.title}</div>
                <div className="text-xs text-slate-500">{c.enrolled_count} enrolled · {c.duration}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  const map = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className={`w-10 h-10 rounded-xl ${map[color]} flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-display font-black text-3xl text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{label}</div>
    </div>
  );
}
